import {
  Anchor,
  Center,
  Checkbox,
  Grid,
  Group,
  InputWrapper,
  Modal,
  Paper,
  Space,
  Text,
  Title,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'tabler-icons-react';
import { BusinessBankAccountDepositTypes } from '../../../enums/bank.enums';
import { BusinessTypes } from '../../../enums/business-type';
import { MerchantOnboardingStatus } from '../../../enums/merchant-onboarding-status';
import { removeEmptyPropertiesWithNested } from '../../../helpers/filters.helpers';
import { getFileFromUpdateFormFile } from '../../../helpers/merchant-onboarding';
import { uploadFiles } from '../../../helpers/upload-files';
import useAuth from '../../../hooks/useAuth';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import DataPrivacy from '../../../pages/common-pages/data-privacy';
import TermsAndConditions from '../../../pages/common-pages/terms_and_conditions';
import { StatementAcceptanceSchema } from '../../../schemas/statement-acceptance.schema';
import { AuthType } from '../../../type/auth.types';
import { ErrorResponse } from '../../../type/error-response.types';
import { MerchantOnboardingFormValues } from '../../../type/merchant-onboarding-form-values.types';
import { OnboardMerchantRequest } from '../../../type/onboard-merchant-request.types';
import { OnboardMerchantResponse } from '../../../type/onboard-merchant-response';
import StepperButton from '../../buttons/stepper.button/stepper.button';
import './statement-acceptance-step.styles.css';
function StatementAcceptanceStep(props: {
  mainForm: UseFormReturnType<MerchantOnboardingFormValues>;
  active: number;
  activated: boolean;
  prevStep: () => void;
}) {
  const { mainForm, active, activated, prevStep } = props;

  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState(false);
  const [privacyOpened, setPrivacyOpened] = useState(false);
  const [termsOpened, setTermsOpened] = useState(false);

  const SUBMIT_ONBOARD_FORM_URL = '/web/onboard';

  const form = useForm<MerchantOnboardingFormValues>({
    initialValues: mainForm.values,
    schema: joiResolver(StatementAcceptanceSchema),
  });

  useEffect(() => {
    if (!activated) {
      return;
    }
    form.setValues(mainForm.values);
  }, [active]);

  const handlePrevStep = () => {
    setLoading(true);
    mainForm.setValues(form.values);
    setLoading(false);
    if (!loading) prevStep();
  };

  const deleteEmptyOnboarding = (
    onBoardMerchantRequestNonFile: OnboardMerchantRequest
  ) => {
    if (form.values.businessWebsiteUrl.trim().length < 1) {
      delete onBoardMerchantRequestNonFile?.websites?.businessWebsiteUrl;
    }
    if (form.values.facebookUrl.trim().length < 1) {
      delete onBoardMerchantRequestNonFile?.websites?.facebookUrl;
    }
    if (form.values.twitterUrl.trim().length < 1) {
      delete onBoardMerchantRequestNonFile?.websites?.twitterUrl;
    }
    if (form.values.instagramUrl.trim().length < 1) {
      delete onBoardMerchantRequestNonFile?.websites?.instagramUrl;
    }
    if (form.values.tiktokUrl.trim().length < 1) {
      delete onBoardMerchantRequestNonFile?.websites?.tiktokUrl;
    }
    if (form.values.aMiddleName.trim().length < 1) {
      delete onBoardMerchantRequestNonFile.representatives.authorized
        .middleName;
    }

    if (form.values.businessTypeId === BusinessTypes.STARTER) {
      delete onBoardMerchantRequestNonFile.legalName;
      delete onBoardMerchantRequestNonFile.businessHandle;
    }

    if (!form.values.mainOfficeBldgNameNo) {
      delete onBoardMerchantRequestNonFile.address.office.buildingNameAndNumber;
    }

    form.values.settlements.forEach((settlement) => {
      if (
        settlement.accountDepositTypeId !== BusinessBankAccountDepositTypes.BANK
      ) {
        delete settlement.accountTypeId;
      }
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    const {
      businessTypeId,

      merchantName,
      merchantBusinessName,
      businessHandle,
      merchantCompanyTin,

      mainOfficeContactNumber,
      mainOfficeBldgNameNo,
      mainOfficeStreetAddress,
      mainOfficeBarangay,
      mainOfficeCity,
      mainOfficeProvince,
      mainOfficePostalCode,

      isBrickAndMortarStore,
      industry,
      serviceDescription,
      modeOfPayment,
      currency,
      aubPartnerId,
      estimatedMonthlyGrossTransaction,

      businessWebsiteUrl,
      facebookUrl,
      twitterUrl,
      instagramUrl,
      tiktokUrl,
      isBusinessWebsiteUnderDevelopment,

      aFirstName,
      aMiddleName,
      aLastName,
      aPosition,
      aEmailAddress,
      aContactNumber,
      aDateOfBirth,

      settlements,

      isWantPackworksWallet,
      isWantPayMayaBankWallet,
      isWantPayMayaBankAccount,

      idsOfValidSignatories,
      mayorsOrBusinessPermit,
      birCert2303,
      secCert,
      gis,
      aoiAndByLaws,
      secretarysCert,
      barangayBusinessPermit,
      storePhoto,

      idsOfValidSignatoriesBack,
      idsOfValidSignatoriesSignature,
      mayorsOrBusinessPermitBack,
      mayorsOrBusinessPermitSignature,
      birCert2303Back,
      birCert2303Signature,
      secCertBack,
      secCertSignature,
      gisBack,
      gisSignature,
      aoiAndByLawsBack,
      aoiAndByLawsSignature,
      secretarysCertBack,
      secretarysCertSignature,
      barangayBusinessPermitBack,
      barangayBusinessPermitSignature,

      aMothersMaidenName,
      aGender,
      aAubNationalityCode,
      aAubDeviceId,
      aAubIdCode,
      aAubIdNumber,
      aAubRegionCode,
    } = form.values;

    const onBoardMerchantRequestNonFile: OnboardMerchantRequest = {
      businessTypeId: businessTypeId,
      merchantName: merchantName,
      legalName: merchantBusinessName,
      currencyId: currency,
      aubPartnerId: aubPartnerId,
      estimatedMonthlyGrossTransaction: estimatedMonthlyGrossTransaction,
      businessHandle: businessHandle,
      tin: merchantCompanyTin,

      address: {
        office: {
          contactNumber: mainOfficeContactNumber,
          buildingNameAndNumber: mainOfficeBldgNameNo,
          line1: mainOfficeStreetAddress,
          barangay: mainOfficeBarangay,
          city: mainOfficeCity,
          state: mainOfficeProvince,
          postalCode: mainOfficePostalCode,
        },
      },
      isBrickAndMortarStore: isBrickAndMortarStore,
      industry: industry,
      serviceDescription: serviceDescription,
      modeOfPaymentId: modeOfPayment,
      websites: {
        businessWebsiteUrl,
        isBusinessWebsiteUnderDevelopment,
        facebookUrl,
        twitterUrl,
        instagramUrl,
        tiktokUrl,
      },

      representatives: {
        authorized: {
          firstName: aFirstName,
          middleName: aMiddleName,
          lastName: aLastName,
          position: aPosition,
          emailAddress: aEmailAddress,
          contactNumber: aContactNumber,
          dateOfBirth: aDateOfBirth!,
          mothersMaidenName: aMothersMaidenName,
          gender: aGender ?? undefined,
          aubNationalityCode: aAubNationalityCode,
          aubDeviceId: aAubDeviceId,
          aubIdCode: aAubIdCode,
          aubIdNumber: aAubIdNumber,
          aubRegionCode: aAubRegionCode,
        },
      },
      banks: settlements,
      isWantPackworksWallet,
      isWantPayMayaWallet: isWantPayMayaBankWallet,
      isWantPayMayaBankAccount,
    };

    deleteEmptyOnboarding(onBoardMerchantRequestNonFile);
    removeEmptyPropertiesWithNested(onBoardMerchantRequestNonFile);

    try {
      const uploadFormFiles = getFileFromUpdateFormFile(form);
      await uploadFiles(uploadFormFiles, auth?.id as number, businessTypeId);
      const response = await axiosPrivate.post(
        SUBMIT_ONBOARD_FORM_URL,
        // formData,
        onBoardMerchantRequestNonFile,
        {
          // headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );
      const resData: OnboardMerchantResponse = response.data;
      const newAuth: AuthType = {
        ...auth,
        onboardingReferenceNumber: resData.requestReferenceNumber,
        onboardingStatus: MerchantOnboardingStatus.NEW,
      };
      setAuth(newAuth);
      navigate('/onboarding/pending');
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      let message = error?.error || 'Failed to submit form';
      showNotification({
        id: 'failed-to-submit-business-starter-form',
        message,
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Modal
          opened={privacyOpened}
          onClose={() => setPrivacyOpened(false)}
          size='80%'
          centered
          data-cy='privacyPolicyModal'
        >
          <DataPrivacy />
        </Modal>
        <Modal
          opened={termsOpened}
          onClose={() => setTermsOpened(false)}
          size='80%'
          centered
          data-cy='termsAndConditionsModal'
        >
          <TermsAndConditions />
        </Modal>
        <Paper
          shadow='xl'
          style={{ maxWidth: 1300 }}
          sx={{
            '@media (max-width: 755px)': {
              padding: 20,
              margin: 10,
            },
            '@media (min-width: 755px)': {
              padding: 100,
            },
            '@media (min-width: 1200px)': {
              padding: 50,
            },
          }}
        >
          <Grid justify='center' grow>
            <Grid.Col>
              <Title style={{ fontSize: '3rem' }} align='center'>
                Statement of Acceptance
              </Title>
            </Grid.Col>
            <Grid.Col>
              <Space />
            </Grid.Col>
            <Grid.Col>
              <Text align='center'>
                I/We have read, understood, and accepted the Merchant Terms and
                Conditions set forth on this Merchant Agreement Enrollment Form
                with Terms and Conditions, and that all information that I/We
                have given here are true and correct.
              </Text>
            </Grid.Col>
            <Grid.Col>
              <Space />
            </Grid.Col>
            <Grid.Col>
              <Text align='center'>
                I/We authorize XPAY.WORLDWIDE CORPORATION ("XPAY") to verify
                such information from whatever sources it may consider
                appropriate <br /> XPAY shall not be liable for any damage,
                claim, suit, liability and/or inconvenience brought about by our
                failure to comply with the above mentioned Terms and Conditions
              </Text>
            </Grid.Col>
            <Grid.Col>
              <Space />
            </Grid.Col>
            <Grid.Col>
              <Center>
                <InputWrapper
                  id='agreeTermsPrivacy'
                  error={form.errors.agreeTermsPrivacy}
                >
                  <Group noWrap>
                    <Checkbox
                      id='agreeTermsPrivacy'
                      onChange={(event) =>
                        form.setFieldValue(
                          'agreeTermsPrivacy',
                          event.currentTarget.checked
                        )
                      }
                      checked={form.values.agreeTermsPrivacy}
                    />
                    <Text>
                      I have read and agree to the{' '}
                      <Anchor
                        onClick={() => {
                          setTermsOpened(true);
                        }}
                        data-cy='termsAndConditionsAnchor'
                      >
                        Terms and Conditions
                      </Anchor>{' '}
                      and the{' '}
                      <Anchor
                        onClick={() => {
                          setPrivacyOpened(true);
                        }}
                        data-cy='privacyPolicyAnchor'
                      >
                        Privacy Policy
                      </Anchor>
                    </Text>
                  </Group>
                </InputWrapper>
              </Center>
            </Grid.Col>
          </Grid>
        </Paper>
        <StepperButton
          prevStep={handlePrevStep}
          active={active}
          loading={loading}
          submit
          nextStep={undefined}
        />
      </form>
    </>
  );
}

export default StatementAcceptanceStep;
